(() => {
  if (customElements.get('ugc-video-reviews')) return;

  class UgcVideoReviews extends HTMLElement {
    connectedCallback() {
      this.initTimer = setTimeout(() => this.init(), 0);
    }

    init() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.track = this.querySelector('[data-ugc-track]');
      this.prevBtn = this.querySelector('[data-ugc-prev]');
      this.nextBtn = this.querySelector('[data-ugc-next]');
      this.navWrap = this.querySelector('[data-ugc-nav]');
      this.dotsContainer = this.querySelector('[data-ugc-dots]');
      this.autoplayOnHover = this.dataset.autoplayHover === 'true';

      if (!this.track) return;

      this.setupCarousel(signal);
      this.setupVideoCards(signal);
      this.setupSubmissionModal(signal);
      this.setAttribute('data-ready', '');
    }

    getSlides() {
      return Array.from(this.track.querySelectorAll('[data-ugc-slide]'));
    }

    setupCarousel(signal) {
      const updateControls = () => {
        const slides = this.getSlides();
        if (!slides.length) return;

        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        const canScroll = maxScroll > 8;

        if (this.navWrap) {
          this.navWrap.hidden = !canScroll;
        }
        if (this.prevBtn) {
          this.prevBtn.disabled = this.track.scrollLeft <= 6;
        }
        if (this.nextBtn) {
          this.nextBtn.disabled = this.track.scrollLeft >= maxScroll - 6;
        }

        // Update active dot
        if (this.dotsContainer) {
          const trackLeft = this.track.getBoundingClientRect().left;
          const activeIndex = slides.reduce((nearest, slide, idx) => {
            const dist = Math.abs(slide.getBoundingClientRect().left - trackLeft);
            const nearestDist = Math.abs(slides[nearest].getBoundingClientRect().left - trackLeft);
            return dist < nearestDist ? idx : nearest;
          }, 0);

          const dots = Array.from(this.dotsContainer.querySelectorAll('.ugc-reviews__dot'));
          dots.forEach((dot, idx) => {
            dot.setAttribute('aria-selected', idx === activeIndex ? 'true' : 'false');
          });
        }
      };

      this.renderDots = () => {
        if (!this.dotsContainer) return;
        const slides = this.getSlides();
        this.dotsContainer.innerHTML = '';
        if (slides.length <= 1) return;

        slides.forEach((_, idx) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'ugc-reviews__dot';
          dot.setAttribute('role', 'tab');
          dot.setAttribute('aria-label', `Go to video review ${idx + 1}`);
          dot.setAttribute('aria-selected', idx === 0 ? 'true' : 'false');
          dot.addEventListener('click', () => this.scrollToSlide(idx), { signal });
          this.dotsContainer.appendChild(dot);
        });
      };

      this.scrollToSlide = (index) => {
        const slides = this.getSlides();
        const target = slides[Math.max(0, Math.min(slides.length - 1, index))];
        if (!target) return;
        const offset = target.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
        this.track.scrollTo({
          left: this.track.scrollLeft + offset,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
        });
      };

      const scrollByStep = (direction) => {
        const slides = this.getSlides();
        if (!slides.length) return;
        const slideWidth = slides[0].getBoundingClientRect().width;
        const gap = parseFloat(getComputedStyle(this.track).gap) || 24;
        this.track.scrollBy({
          left: direction * (slideWidth + gap),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
        });
      };

      this.prevBtn?.addEventListener('click', () => scrollByStep(-1), { signal });
      this.nextBtn?.addEventListener('click', () => scrollByStep(1), { signal });
      this.track.addEventListener('scroll', updateControls, { signal, passive: true });

      this.resizeObserver = new ResizeObserver(updateControls);
      this.resizeObserver.observe(this.track);

      this.renderDots();
      updateControls();
      this.updateControls = updateControls;
    }

    setupVideoCards(signal) {
      this.bindCardEvents = (card) => {
        if (card.dataset.bound === 'true') return;
        card.dataset.bound = 'true';

        const mediaBox = card.querySelector('[data-ugc-media]');
        const video = card.querySelector('video');
        const muteBtn = card.querySelector('[data-ugc-mute]');
        const progressBar = card.querySelector('[data-ugc-progress]');
        let demoTimer = null;
        let demoProgress = 0;

        const pauseOtherCards = () => {
          this.querySelectorAll('.ugc-card.is-playing').forEach((otherCard) => {
            if (otherCard !== card) {
              otherCard.stopPlayback?.();
            }
          });
        };

        const startPlayback = () => {
          pauseOtherCards();
          card.classList.add('is-playing');
          if (video) {
            video.play().catch(() => {});
          } else {
            // Demo simulation when no video file is attached yet
            clearInterval(demoTimer);
            demoTimer = setInterval(() => {
              demoProgress = (demoProgress + 2) % 100;
              if (progressBar) progressBar.style.width = `${demoProgress}%`;
            }, 160);
          }
        };

        const stopPlayback = () => {
          card.classList.remove('is-playing');
          if (video) {
            video.pause();
          } else {
            clearInterval(demoTimer);
          }
        };

        card.stopPlayback = stopPlayback;

        const togglePlay = (event) => {
          if (event.target.closest('[data-ugc-mute]')) return;
          if (card.classList.contains('is-playing')) {
            stopPlayback();
          } else {
            startPlayback();
          }
        };

        mediaBox?.addEventListener('click', togglePlay, { signal });

        if (this.autoplayOnHover && window.matchMedia('(hover: hover)').matches) {
          mediaBox?.addEventListener('mouseenter', startPlayback, { signal });
          mediaBox?.addEventListener('mouseleave', stopPlayback, { signal });
        }

        if (video) {
          video.addEventListener('timeupdate', () => {
            if (!video.duration || !progressBar) return;
            const pct = (video.currentTime / video.duration) * 100;
            progressBar.style.width = `${pct}%`;
          }, { signal });

          video.addEventListener('ended', () => {
            if (!video.loop) {
              card.classList.remove('is-playing');
              if (progressBar) progressBar.style.width = '0%';
            }
          }, { signal });
        }

        muteBtn?.addEventListener('click', (event) => {
          event.stopPropagation();
          const isUnmuted = card.classList.toggle('is-unmuted');
          muteBtn.setAttribute('aria-pressed', isUnmuted ? 'true' : 'false');
          muteBtn.setAttribute('aria-label', isUnmuted ? 'Mute video' : 'Unmute video');
          if (video) {
            video.muted = !isUnmuted;
          }
        }, { signal });
      };

      this.querySelectorAll('.ugc-card').forEach((card) => this.bindCardEvents(card));
    }

    setupSubmissionModal(signal) {
      const openBtn = this.querySelector('[data-ugc-open-modal]');
      const modal = this.querySelector('[data-ugc-modal]');
      if (!openBtn || !modal) return;

      const closeBtns = modal.querySelectorAll('[data-ugc-close-modal]');
      const form = modal.querySelector('[data-ugc-submit-form]');
      const fileInput = modal.querySelector('[data-ugc-file-input]');
      const urlInput = modal.querySelector('[data-ugc-url-input]');
      const dropzone = modal.querySelector('[data-ugc-dropzone]');
      const previewWrap = modal.querySelector('[data-ugc-preview]');
      const previewVideo = modal.querySelector('[data-ugc-preview-video]');
      const previewName = modal.querySelector('[data-ugc-preview-name]');
      const removeFileBtn = modal.querySelector('[data-ugc-remove-file]');
      const successBox = modal.querySelector('[data-ugc-success]');
      const resetAfterSuccessBtn = modal.querySelector('[data-ugc-close-success]');

      let uploadedObjectUrl = null;

      const openModal = () => {
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        const firstInput = modal.querySelector('input[name="author_name"]');
        setTimeout(() => firstInput?.focus(), 60);
      };

      const closeModal = () => {
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        openBtn.focus();
      };

      openBtn.addEventListener('click', openModal, { signal });
      closeBtns.forEach((btn) => btn.addEventListener('click', closeModal, { signal }));

      window.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') {
          closeModal();
        }
      }, { signal });

      const handleFileSelect = (file) => {
        if (!file || !file.type.startsWith('video/')) return;
        if (uploadedObjectUrl) URL.revokeObjectURL(uploadedObjectUrl);
        uploadedObjectUrl = URL.createObjectURL(file);
        if (previewVideo) {
          previewVideo.src = uploadedObjectUrl;
          previewVideo.load();
        }
        if (previewName) {
          const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
          previewName.textContent = `${file.name} (${sizeMb} MB)`;
        }
        previewWrap?.classList.add('is-visible');
      };

      fileInput?.addEventListener('change', (event) => {
        const file = event.target.files?.[0];
        handleFileSelect(file);
      }, { signal });

      dropzone?.addEventListener('dragover', (event) => {
        event.preventDefault();
        dropzone.classList.add('is-dragover');
      }, { signal });

      dropzone?.addEventListener('dragleave', () => {
        dropzone.classList.remove('is-dragover');
      }, { signal });

      dropzone?.addEventListener('drop', (event) => {
        event.preventDefault();
        dropzone.classList.remove('is-dragover');
        const file = event.dataTransfer?.files?.[0];
        if (file && fileInput) {
          handleFileSelect(file);
        }
      }, { signal });

      removeFileBtn?.addEventListener('click', () => {
        if (fileInput) fileInput.value = '';
        if (previewVideo) previewVideo.removeAttribute('src');
        previewWrap?.classList.remove('is-visible');
      }, { signal });

      form?.addEventListener('submit', (event) => {
        event.preventDefault();
        const formData = new FormData(form);
        const author = (formData.get('author_name') || 'Valued Client').toString().trim();
        const handle = (formData.get('author_handle') || '').toString().trim();
        const variantInfo = (formData.get('variant_info') || '').toString().trim();
        const quote = (formData.get('review_text') || '').toString().trim();
        const rating = Number.parseInt(formData.get('rating'), 10) || 5;
        const externalVideoUrl = (urlInput?.value || '').trim();
        const videoSrc = uploadedObjectUrl || externalVideoUrl;

        // Dynamically append the submitted UGC video review card to the carousel for instant preview
        this.appendDynamicReviewCard({
          author,
          handle,
          variantInfo,
          quote,
          rating,
          videoSrc
        });

        form.style.display = 'none';
        successBox?.classList.add('is-visible');
      }, { signal });

      resetAfterSuccessBtn?.addEventListener('click', () => {
        closeModal();
        setTimeout(() => {
          form?.reset();
          if (form) form.style.display = '';
          successBox?.classList.remove('is-visible');
          previewWrap?.classList.remove('is-visible');
        }, 300);
      }, { signal });
    }

    appendDynamicReviewCard({ author, handle, variantInfo, quote, rating, videoSrc }) {
      if (!this.track) return;

      const li = document.createElement('li');
      li.className = 'ugc-reviews__slide';
      li.setAttribute('data-ugc-slide', '');

      const stars = '★'.repeat(Math.max(1, Math.min(5, rating)));
      const fallbackImg = this.dataset.productFallbackImage || '';

      const mediaHtml = videoSrc
        ? `<video class="ugc-card__video" src="${this.escapeAttr(videoSrc)}" playsinline loop muted preload="metadata"></video>`
        : `<div class="ugc-card__demo-stage">${fallbackImg ? `<img src="${this.escapeAttr(fallbackImg)}" alt="${this.escapeAttr(author)}" loading="lazy">` : ''}</div>`;

      li.innerHTML = `
        <article class="ugc-card">
          <div class="ugc-card__media" data-ugc-media>
            ${mediaHtml}
            <div class="ugc-card__top-bar">
              <span class="ugc-card__pill">Just Submitted</span>
              <button type="button" class="ugc-card__mute-btn" data-ugc-mute aria-label="Unmute video" aria-pressed="false">
                <svg class="icon-muted" viewBox="0 0 24 24"><path d="M11 5L6 9H2v6h4l5 4V5z"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
                <svg class="icon-unmuted" viewBox="0 0 24 24"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>
              </button>
            </div>
            <button type="button" class="ugc-card__play-btn" aria-label="Play video review">
              <svg viewBox="0 0 24 24"><polygon points="6 3 20 12 6 21 6 3"/></svg>
            </button>
            <div class="ugc-card__media-footer">
              <div class="ugc-card__author-wrap">
                <div class="ugc-card__author-row">
                  <span class="ugc-card__author">${this.escapeHtml(author)}</span>
                  <span class="ugc-card__verified">
                    <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                    Verified
                  </span>
                </div>
                ${handle ? `<span class="ugc-card__handle">${this.escapeHtml(handle)}</span>` : ''}
              </div>
            </div>
            <div class="ugc-card__progress"><span class="ugc-card__progress-bar" data-ugc-progress></span></div>
          </div>
          <div class="ugc-card__body">
            <div class="ugc-card__rating-row">
              <span class="ugc-card__stars" aria-label="${rating} out of 5 stars">${stars}</span>
              ${variantInfo ? `<span class="ugc-card__variant-tag">${this.escapeHtml(variantInfo)}</span>` : ''}
            </div>
            <p class="ugc-card__quote">&ldquo;${this.escapeHtml(quote)}&rdquo;</p>
          </div>
        </article>
      `;

      this.track.insertBefore(li, this.track.firstElementChild);
      const newCard = li.querySelector('.ugc-card');
      if (newCard) this.bindCardEvents(newCard);
      this.renderDots?.();
      this.updateControls?.();
      this.scrollToSlide?.(0);
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    escapeAttr(str) {
      return String(str).replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    disconnectedCallback() {
      clearTimeout(this.initTimer);
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.removeAttribute('data-ready');
    }
  }

  customElements.define('ugc-video-reviews', UgcVideoReviews);
})();
